import React from "react";
import { Redirect, Route, Router, Switch, useLocation } from "wouter";
import { AppLayout } from "./layouts/AppLayout";
import { Dashboard } from "./pages/Dashboard";
import { Login } from "./pages/auth/Login";
import { Registro } from "./pages/auth/Registro";
import { RecuperarContrasena } from "./pages/auth/RecuperarContrasena";
import { ResetPassword } from "./pages/auth/ResetPassword";
import { ConfirmarMandante } from "./pages/auth/ConfirmarMandante";
import { ConfirmarContratista } from "./pages/auth/ConfirmarContratista";
import { AceptarVinculacion } from "./pages/auth/AceptarVinculacion";
import { Terminos } from "./pages/legal/Terminos";
import { Privacidad } from "./pages/legal/Privacidad";
import { Tutoriales } from "./pages/tutoriales/Tutoriales";
import { Perfil } from "./pages/usuario/Perfil";
import { ResumenCupos } from "./pages/licencias/ResumenCupos";
import { AsignacionUsuarios } from "./pages/licencias/AsignacionUsuarios";
import { BandejaEntrada } from "./pages/documentos/BandejaEntrada";
import { Trabajadores } from "./pages/personas/Trabajadores";
import { FichaTrabajador } from "./pages/personas/FichaTrabajador";
import { Cargos } from "./pages/personas/Cargos";
import { Areas } from "./pages/personas/Areas";
import { Contratos } from "./pages/comercial/Contratos";
import { Contratistas } from "./pages/comercial/Contratistas";
import { Mandantes } from "./pages/comercial/Mandantes";
import { EPP } from "./pages/recursos/EPP";
import { Vehiculos } from "./pages/recursos/Vehiculos";
import { FichaVehiculo } from "./pages/recursos/FichaVehiculo";
import { Maquinaria } from "./pages/recursos/Maquinaria";
import { FichaMaquinaria } from "./pages/recursos/FichaMaquinaria";
import { Metas } from "./pages/control/Metas";
import { Cumplimiento } from "./pages/control/Cumplimiento";
import { Empresa } from "./pages/configuracion/Empresa";
import { Capacitaciones } from "./pages/configuracion/Capacitaciones";
import { ProgramaCapacitaciones } from "./pages/capacitaciones/ProgramaCapacitaciones";
import { FichaCapacitacion } from "./pages/capacitaciones/FichaCapacitacion";
import { Suscripcion } from "./pages/configuracion/Suscripcion";
import { OnboardingWizard } from "./pages/onboarding/OnboardingWizard";
import { Novedades } from "./pages/novedades/Novedades";
import { UserProvider, useUser } from "./contexts/UserContext";
import { useSetupStatus } from "./hooks/useSetupStatus";
import { GuideProvider } from "./contexts/GuideContext";
import { ActiveGuideProvider } from "./contexts/ActiveGuideContext";
import { SetupStatusProvider } from "./contexts/SetupStatusContext";
import { HelpResourcesFab } from "./components/HelpResourcesFab";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useUser();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Redirect to="/login" />;
  return <>{children}</>;
}

// TODO test (guard — usuario nuevo): wizard_pending=true y wizard_saltado=false
//   → cualquier ruta que no sea /onboarding redirige a /onboarding
// TODO test (guard — setup completo): wizard_pending=false → no redirige
// TODO test (guard — wizard saltado): wizard_saltado=true → no redirige aunque wizard_pending=true
// TODO test (guard — ya en /onboarding): si ya está en /onboarding no genera redirect loop
// TODO test (guard — loading): muestra spinner mientras isLoading del setup-status es true

function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const { status, isLoading } = useSetupStatus();

  // Mientras carga el setup-status mostramos spinner para evitar flash de contenido incorrecto
  if (isLoading && !status) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  // Guard: si el wizard está pendiente y el usuario no está ya en /onboarding, redirige.
  // El onboarding es obligatorio: no hay escape. El flag wizard_saltado ya no se consulta
  // (el endpoint /wizard/saltar quedó inerte; su remoción backend es follow-up).
  if (status?.wizard_pending === true && location !== '/onboarding') {
    return <Redirect to="/onboarding" />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <UserProvider>
      {/* Único Provider de tooltips de la app. Agrupa a todos para que, una vez
          abierto el primero, los siguientes aparezcan sin volver a esperar.

          Es el único de verdad desde que se sacó el que `SidebarProvider` traía
          anidado: como ese envuelve toda la aplicación autenticada, lo de acá
          no llegaba a ninguna pantalla real y el retardo efectivo era 0. Si se
          prefieren instantáneos, el número a tocar es éste y ninguno más. */}
      <TooltipProvider delayDuration={250} skipDelayDuration={300}>
      <Router base="/">
        <Switch>
          {/* Auth Routes - Sin AppLayout */}
          <Route path="/login" component={Login} />
          <Route path="/registro" component={Registro} />
          <Route path="/recuperar-contrasena" component={RecuperarContrasena} />
          <Route path="/reset-password" component={ResetPassword} />
          <Route path="/confirmar-mandante" component={ConfirmarMandante} />
          <Route path="/confirmar-contratista" component={ConfirmarContratista} />
          <Route path="/aceptar-vinculacion" component={AceptarVinculacion} />

          {/* Páginas legales públicas - Sin AppLayout */}
          <Route path="/terminos" component={Terminos} />
          <Route path="/privacidad" component={Privacidad} />

          {/* Tutoriales en video - Público (accesible también desde fc-app) */}
          <Route path="/tutoriales" component={Tutoriales} />

          {/* Onboarding Wizard - Autenticado, layout propio (sin AppLayout) */}
          <Route path="/onboarding">
            <PrivateRoute>
              <OnboardingWizard />
            </PrivateRoute>
          </Route>

          {/* App Routes - Con AppLayout + Guard de onboarding */}
          <Route>
            <PrivateRoute>
            <OnboardingGuard>
            <AppLayout>
            <GuideProvider>
            <SetupStatusProvider>
            <ActiveGuideProvider>
              <Switch>
                <Route path="/" component={Dashboard} />
                
                {/* Usuario */}
                <Route path="/usuario/perfil" component={Perfil} />
                <Route path="/novedades" component={Novedades} />
                
                {/* Gestión de Licencias y App */}
                <Route path="/licencias/resumen-cupos" component={ResumenCupos} />
                <Route path="/licencias/asignacion-usuarios" component={AsignacionUsuarios} />
                
                {/* Gestión Documental */}
                <Route path="/documentos/bandeja-entrada">
                  <BandejaEntrada />
                </Route>
                
                {/* Organización y Personas */}
                <Route path="/personas/trabajadores" component={Trabajadores} />
                <Route path="/personas/trabajadores/:uuid/ficha" component={FichaTrabajador} />
                <Route path="/personas/cargos" component={Cargos} />
                <Route path="/personas/areas" component={Areas} />
                
                {/* Gestión Comercial */}
                <Route path="/comercial/contratos" component={Contratos} />
                <Route path="/comercial/contratistas" component={Contratistas} />
                <Route path="/comercial/mandantes" component={Mandantes} />
                
                {/* Recursos e Inventario */}
                <Route path="/recursos/epp" component={EPP} />
                <Route path="/recursos/vehiculos" component={Vehiculos} />
                <Route path="/vehiculos/:id/ficha" component={FichaVehiculo} />
                <Route path="/recursos/maquinaria" component={Maquinaria} />
                <Route path="/maquinaria/:id/ficha" component={FichaMaquinaria} />
                
                {/* Control de Gestión */}
                <Route path="/control/metas" component={Metas} />
                <Route path="/control/cumplimiento" component={Cumplimiento} />
                
                {/* Configuración */}
                <Route path="/configuracion/empresa" component={Empresa} />
                <Route path="/configuracion/capacitaciones" component={Capacitaciones} />
                <Route path="/capacitaciones/programa" component={ProgramaCapacitaciones} />
                <Route
                  path="/capacitaciones/programa/tematica/:tematicaId"
                  component={FichaCapacitacion}
                />
                <Route path="/configuracion/suscripcion" component={Suscripcion} />

                <Route>
                  <div className="flex h-full items-center justify-center">
                    <div className="text-center">
                      <h1 className="text-4xl font-bold">404</h1>
                      <p className="text-muted-foreground">Página no encontrada</p>
                    </div>
                  </div>
                </Route>
              </Switch>
              <HelpResourcesFab />
            </ActiveGuideProvider>
            </SetupStatusProvider>
            </GuideProvider>
            </AppLayout>
            </OnboardingGuard>
            </PrivateRoute>
          </Route>
        </Switch>
      </Router>
      <Toaster />
      </TooltipProvider>
    </UserProvider>
  );
}

export default App;
